import { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../Components/Navbar/AdminNavbar/AdminNavbar";
import "./Directory.css";

export default function Directory() {
  const [cats, setCats] = useState([]);
  const [islands, setIslands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [rows, setRows] = useState([]);

  const [qName, setQName] = useState("");
  const [tempQName, setTempQName] = useState("");

  const [catSearch, setCatSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");

  const [countryFilter, setCountryFilter] = useState("");
  const [islandFilter, setIslandFilter] = useState("");
  const [selectedIslandLabel, setSelectedIslandLabel] = useState("");

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showIslandMenu, setShowIslandMenu] = useState(false);
  const [activeIslandGroup, setActiveIslandGroup] = useState("");

  const [searchParams] = useSearchParams();
  const wrapperRef = useRef(null);

  /* close autocomplete on outside click */
  useEffect(() => {
    function onClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setShowIslandMenu(false);
        setActiveIslandGroup("");
      }
    }

    document.addEventListener("mousedown", onClick);

    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  /* load categories */
  useEffect(() => {
    (async () => {
      const q = query(
        collection(db, "categories"),
        where("enabled", "==", true),
        orderBy("name")
      );

      const snap = await getDocs(q);

      setCats(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    })();
  }, []);

  /* load islands */
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "islands"));

      setIslands(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
          .filter((island) => island.enabled !== false && island.name)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    })();
  }, []);

  /* load country groupings */
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "country"));

      setCountries(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
          .filter((country) => country.enabled !== false && country.name)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    })();
  }, []);

  /* load businesses */
  useEffect(() => {
    (async () => {
      const q = query(
        collection(db, "businesses"),
        where("is_published", "==", true),
        orderBy("business_name")
      );

      const snap = await getDocs(q);

      setRows(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    })();
  }, []);

  /* map → directory island filter */
  useEffect(() => {
    const island = searchParams.get("island");
    const category = searchParams.get("category");

    if (island || category) {
      setQName("");
      setTempQName("");
      setCatSearch(category || "");
      setCatFilter(category || "");
      setCountryFilter("");
      setIslandFilter(island?.toLowerCase() || "");
      setSelectedIslandLabel(formatLocationName(island || ""));
    }
  }, [searchParams]);

  const { islandGroups, standaloneIslands } = buildIslandMenu(islands, countries);
  const selectedCountryTerms = (() => {
    const selectedGroup = islandGroups.find(
      (group) => normalizeLocation(group.label) === normalizeLocation(countryFilter)
    );

    if (!selectedGroup) return getSelectedLocationTerms(countryFilter);

    return [
      groupTerms(selectedGroup.label),
      ...selectedGroup.options.map(([, value]) => groupTerms(value)),
    ].flat();
  })();

  function selectIsland(value, label = formatLocationName(value)) {
    setCountryFilter(value);
    setIslandFilter("");
    setSelectedIslandLabel(label);
    setShowIslandMenu(false);
    setActiveIslandGroup("");
  }

  /* GO button search */
  function handleSearch() {
    setQName(tempQName);
  }

  /* enter key support */
  function handleKeyDown(e) {
    if (e.key === "Enter") {
      handleSearch();
    }
  }

  /* filtering */
  const filtered = rows
    .filter((r) => {
      const hasName = qName.trim() !== "";
      const hasCategory = catFilter !== "";
      const hasCountry = countryFilter !== "";
      const hasIsland = islandFilter !== "";

      const nameMatch =
        hasName &&
        r.business_name
          ?.toLowerCase()
          .includes(qName.trim().toLowerCase());

      let categoryMatch = false;

      if (hasCategory) {
        const cat = cats.find(
          (c) => c.name.toLowerCase() === catFilter.toLowerCase()
        );

        if (cat && Array.isArray(r.categoryIds)) {
          categoryMatch = r.categoryIds.includes(cat.id);
        }
      }

      const businessLocations = getBusinessLocations(r);

      const countryMatch =
        hasCountry &&
        businessLocations.some((loc) =>
          [loc.country, loc.island]
            .filter(Boolean)
            .some((value) =>
              selectedCountryTerms.includes(normalizeLocation(value))
            )
        );

      const islandMatch =
        hasIsland &&
        businessLocations.some((loc) =>
          [loc.island, loc.country]
            .filter(Boolean)
            .some(
              (value) =>
                normalizeLocation(value) === normalizeLocation(islandFilter)
            )
        );

      /* HARD LOCATION GUARD */
      if (hasIsland && !islandMatch) return false;

      if (hasCountry && !countryMatch) return false;

      if (hasName && !nameMatch) return false;

      if (hasCategory && !categoryMatch) return false;

      return true;
    })
    .sort((a, b) => {
      const rank = {
        featured: 0,
        basic: 1,
        free: 2,
      };

      return (
        (rank[a.listingTier || "free"] ?? 2) -
        (rank[b.listingTier || "free"] ?? 2)
      );
    });

  return (
    <div>
      <Navbar />

      <div className="directory">
        <h2 className="directoryTitle">
          Business Directory
        </h2>

        {/* NEW SINGLE ROW SEARCH BAR */}
        <div className="directoryTopSearch" ref={wrapperRef}>
          {/* BUSINESS NAME */}
          <input
            className="directoryInput"
            placeholder="Search by Business Name"
            value={tempQName}
            onChange={(e) => setTempQName(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          {/* CATEGORY SEARCH */}
          <div className="autocompleteWrapper">
            <input
              className="directoryInput"
              placeholder="Search by Category"
              value={catSearch}
              onChange={(e) => {
                const v = e.target.value;

                setCatSearch(v);
                setShowSuggestions(true);

                if (!v.trim()) {
                  setCatFilter("");
                }
              }}
              onKeyDown={handleKeyDown}
            />

            {showSuggestions && catSearch.length >= 1 && (//if mark wants one filter instead of 2
              <div className="autocompleteList">
                {cats
                  .filter((c) =>
                    c.name
                      .toLowerCase()
                      .includes(catSearch.toLowerCase())
                  )
                  .map((c) => (
                    <div
                      key={c.id}
                      className="autocompleteItem"
                      onClick={() => {
                        setCatSearch(c.name);
                        setCatFilter(c.name);
                        setShowSuggestions(false);
                      }}
                    >
                      {c.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* ISLAND FILTER */}
          <div className="islandDropdown">
            <button
              type="button"
              className="directorySelect islandDropdownButton"
              aria-haspopup="menu"
              aria-expanded={showIslandMenu}
              onClick={() => setShowIslandMenu((open) => !open)}
            >
              <span>{selectedIslandLabel || countryFilter || islandFilter || "Search all Islands"}</span>
              <span className="islandDropdownChevron" aria-hidden="true">▾</span>
            </button>

            {showIslandMenu && (
              <div className="islandDropdownMenu" role="menu">
                <button type="button" role="menuitem" onClick={() => selectIsland("", "")}>
                  Search all Islands
                </button>

                {islandGroups.map((group) => (
                  <div
                    className={`islandGroup ${activeIslandGroup === group.label ? "is-active" : ""}`}
                    key={group.label}
                    onMouseEnter={() => setActiveIslandGroup(group.label)}
                    onMouseLeave={() => setActiveIslandGroup("")}
                  >
                    <button
                      type="button"
                      className="islandGroupButton"
                      aria-haspopup="menu"
                      aria-expanded={activeIslandGroup === group.label}
                      onClick={() => setActiveIslandGroup((active) => active === group.label ? "" : group.label)}
                      onFocus={() => setActiveIslandGroup(group.label)}
                    >
                      <span>{group.label}</span>
                      <span aria-hidden="true">›</span>
                    </button>

                    {activeIslandGroup === group.label && (
                      <div className="islandSubmenu" role="menu" aria-label={group.label}>
                        <button
                          type="button"
                          role="menuitem"
                          className="islandGroupAll"
                          onClick={() => selectIsland(group.label, `All ${group.label}`)}
                        >
                          All {group.label}
                        </button>
                        {group.options.map(([label, value]) => (
                          <button
                            type="button"
                            role="menuitem"
                            key={`${group.label}-${label}`}
                            onClick={() => selectIsland(value, label)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {standaloneIslands.map((name) => (
                  <button type="button" role="menuitem" key={name} onClick={() => selectIsland(name)}>
                    {formatLocationName(name)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* GO BUTTON */}
          <button
            className="directoryGoButton"
            onClick={handleSearch}
          >
            Go
          </button>

          {/* CLEAR */}
          <button
            className="clearButton"
            onClick={() => {
              setQName("");
              setTempQName("");
              setCatSearch("");
              setCatFilter("");
              setCountryFilter("");
              setIslandFilter("");
              setSelectedIslandLabel("");
              setShowIslandMenu(false);
              setActiveIslandGroup("");
            }}
          >
            Clear
          </button>
        </div>

        <div className="directoryMain">
          <div className="directoryResults">
            {filtered.length === 0 ? (
              <div className="directoryEmpty">
                No businesses match your filters.
              </div>
            ) : (
              <div className="directoryList">
                {filtered.map((r) => (
                  <Link
                    key={r.id}
                    to={`/business/${r.id}`}
                    className="directoryItem"
                  >
                    <div className="directoryName">
                      {r.business_name}
                    </div>

                    <div className="directoryMeta">
                      {getLocationSummary(r)
                        .filter(Boolean)
                        .join(", ")}
                    </div>

                    {r.about_short && (
                      <div className="directoryDesc">
                        {r.about_short
                          .split(/\s+/)
                          .slice(0, 60)
                          .join(" ")}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getBusinessLocations(business) {
  if (Array.isArray(business.locations) && business.locations.length > 0) {
    return business.locations.filter(Boolean);
  }

  return [
    {
      country: business.country || "",
      island: business.island || "",
      city: business.city || "",
      address: business.locationLabel || business.location?.address || "",
    },
  ].filter((loc) => loc.country || loc.island || loc.city || loc.address);
}

function getLocationSummary(business) {
  const locations = getBusinessLocations(business);
  const names = locations
    .map((loc) => loc.island || loc.country || loc.city || loc.address)
    .filter(Boolean);

  return [...new Set(names.map(formatLocationName))];
}

function normalizeLocation(value = "") {
  return value
    .toLowerCase()
    .replace(/\bsaint\b/g, "st")
    .replace(/[^a-z0-9]/g, "");
}

function formatLocationName(value = "") {
  const normalized = normalizeLocation(value);
  if (["exuma", "exumas", "exumaislands"].includes(normalized)) {
    return "Exuma Islands";
  }
  return value.replace(/\bSaint\b/gi, "St.");
}

function getSelectedLocationTerms(value) {
  if (value === "Nassau/New Providence") {
    return ["nassau", "newprovidence", "nassaunewprovidence"];
  }
  if (["exuma", "exumas", "exumaislands"].includes(normalizeLocation(value))) {
    return ["exuma", "exumas", "exumaislands"];
  }

  return [normalizeLocation(value)];
}

function formatTerritoryIslandName(islandName, territoryName) {
  const territoryAliases = {
    usvi: "USVI",
    usvirginislands: "USVI",
    unitedstatesvirginislands: "USVI",
    bvi: "BVI",
    britishvirginislands: "BVI",
  };
  const territoryLabel = territoryAliases[normalizeLocation(territoryName)];
  const islandLabel = formatLocationName(islandName);

  return territoryLabel ? `${islandLabel} (${territoryLabel})` : islandLabel;
}


function groupTerms(value) {
  return getSelectedLocationTerms(value);
}

function buildIslandMenu(islands, countries) {
  const usedNames = new Set();
  const standaloneByName = new Map();
  const islandGroups = [];

  countries.forEach((country) => {
    const slugNames = (Array.isArray(country.slugs) ? country.slugs : [])
      .map((entry) => typeof entry === "object" ? entry.name || entry.slug : entry)
      .filter(Boolean)
      .map(String);

    const uniqueSlugs = [...new Map(
      slugNames.map((name) => [normalizeLocation(name), name])
    ).values()];
    const childIslands = uniqueSlugs.filter(
      (name) => normalizeLocation(name) !== normalizeLocation(country.name)
    );

    if (childIslands.length > 0) {
      childIslands.forEach((name) => usedNames.add(normalizeLocation(name)));
      usedNames.add(normalizeLocation(country.name));
      islandGroups.push({
        label: formatLocationName(country.name),
        options: childIslands
          .sort((a, b) => a.localeCompare(b))
          .map((name) => [formatTerritoryIslandName(name, country.name), name]),
      });
      return;
    }

    const standaloneName = uniqueSlugs[0] || country.name;
    standaloneByName.set(normalizeLocation(standaloneName), standaloneName);
    usedNames.add(normalizeLocation(standaloneName));
  });

  islands.forEach((island) => {
    const key = normalizeLocation(island.name);
    if (!usedNames.has(key)) {
      standaloneByName.set(key, island.name);
    }
  });

  islandGroups.sort((a, b) => a.label.localeCompare(b.label));
  const standaloneIslands = [...standaloneByName.values()]
    .sort((a, b) => a.localeCompare(b));

  return { islandGroups, standaloneIslands };
}
